import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import {
  FilesCounts,
  FilesView,
  FilesViewsClient,
  SchemaAttributes,
  SortingDescriptor,
  FilesViewSchema,
  MinimalFilesViewSchema,
} from 'api/clients/FilesViewsClient'
import { FolderClient } from 'api/clients/FolderClient'
import { Assignee, Property, PropertyValue } from 'api/clients/PropertyClient'
import { PaginatedApiClient } from 'api/PaginatedApiClient'
import { ArticleService } from 'api/services/ArticleService'
import { PropertyService } from 'api/services/PropertyService'
import { useLocalDebouncedFn } from 'hooks/useLocalDebouncedFn'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { useHandbook } from 'components/Handbook2_0/hooks'
import { useFiles, useFilesSchema } from 'hooks/queries/finder/useFiles'
import { useFilesCounts } from 'hooks/queries/finder/useFilesCounts'
import { useAuth } from 'hooks/UseAuth'
import { useRouteSchemaId } from 'hooks/useRouteSchemaId'
import produce, { Draft as WritableDraft } from 'immer'
import { differenceBy, findIndex, flattenDeep, isEqual, last, some, uniqBy } from 'lodash'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { DragStart, DropResult } from 'react-beautiful-dnd'
import { useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { clearFinderRefresh, setOpenFolders } from 'redux/actions'
import { deleteFilesProperties } from 'redux/actions/filesProperties'
import { upsertProperties } from 'redux/actions/properties'
import { SuccessResponse } from 'request-dot-js'
import { AlmanacFile as File, FetchRequestState, RootState, ViewType, AlmanacFile } from 'types'
import { buildErrorMessage, pluralize } from 'utils'
import { reportError } from 'utils/error'
import { getFolderPath } from 'utils/urlUtils'
import {
  COLUMN_MIN_WIDTH,
  FIRST_COLUMN_DEFAULT_WIDTH,
  useColumnWidthsStore,
} from './components/views/TableView/stores/useColumnWidthsStore'
import ConfirmationModalStore from 'stores/ConfirmationModalStore'
import { FinderContextValue, FinderProviderProps, TreeFile, TreeFilesView, RefreshPayload } from './FinderContextTypes'
import { useFileViewUpdateRealtime, useItemsRealtime, useViewsChannelRealtime } from './hooks/useRealtime'
import { DEFAULT_LOCATIONS, fileToAssignee } from './Utils'
import { getUniqueIdentifier } from 'utils/sessionStorage'
import { useLoadAssignedProperties } from 'hooks/useLoadAssignedProperties'
import { useRootStore } from 'stores/RootStore'
import { MediaFileClient } from 'api/clients/MediaFileClient'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { DesignClient } from 'api/clients/DesignClient'

type TreeNode = { node: WritableDraft<TreeFile>; parent: WritableDraft<TreeFile> | null }
type NullableTreeNode = TreeNode | null

/*
 * The FinderContext is responsible for fetching and storing all the
 * information for a Files View, and making this available to all the components
 * below it.
 *
 * Methods to manipulate the schema, as well as loading new pages of files, are
 * also exposed by the context.
 */
const FinderContext = createContext<FinderContextValue>({
  addPropertyToSchema: () => {},
  allSelected: false,
  canAddToHandbook: true,
  canCopySelection: true,
  canDeleteSelection: true,
  canMoveSelection: true,
  canShareSelection: true,
  closeFile: () => {},
  closeListFile: () => {},
  refreshAfterExternalChange: () => {},
  dragging: false,
  dropDestination: null,
  getAssigneesForUpdate: () => {},
  handleClick: () => {},
  handleDragEnd: () => {},
  handleDragStart: () => {},
  handleDragUpdate: () => {},
  handleSort: async () => {},
  handleToggleSelectAll: () => {},
  hasMore: false,
  filesResult: undefined,
  isSelected: () => false,
  loadingState: FetchRequestState.Loading,
  loadMore: () => {},
  onChangeEmoji: () => {},
  onCopyRequest: () => {},
  onDeleteRequest: () => {},
  onMoveRequest: () => {},
  onRename: () => {},
  openFile: () => undefined,
  openListFile: () => undefined,
  openFolderIds: [],
  quickAddFile: (file: TreeFile) => undefined,
  refresh: () => {},
  removePropertyFromSchema: () => {},
  removePropertyFromHistorical: () => {},
  resetSelection: () => {},
  selectedColumnIndex: null,
  setSelectedColumnIndex: () => {},
  selectedFiles: [],
  selectFiles: () => {},
  showEmptyState: false,
  showExpandButton: false,
  showWarmState: false,
  updatePropertyValues: () => {},
  updateSchema: () => new Promise(() => {}),
  updateAllSchemas: () => {},
  updateDefaultSchema: () => {},
  setActiveSchema: () => {},
  updateSchemaProperty: () => {},
  viewType: undefined,
})

const FinderContextProvider = FinderContext.Provider

export const FinderProvider = ({ workspaceId, location = 'root', children }: FinderProviderProps) => {
  const folderId = DEFAULT_LOCATIONS.includes(location) ? undefined : location
  const auth = useAuth()
  const dispatch = useDispatch()
  const handbook = useHandbook()
  const { handbooks: handbooksStore } = useRootStore()
  const { files } = useRootStore()
  const schemasStore = useRootStore().filesViewSchemas
  const workspace = useSelector(
    ({ workspace }: RootState) => workspace.workspaces.find((w) => w.id === workspaceId),
    isEqual,
  )
  const filesProperties = useSelector(({ filesProperties }: RootState) => filesProperties)
  const workspaceKey = workspace?.key
  const openFoldersNamespaceKey = `workspace-${workspaceId}-user-${auth?.id}`
  const openFolderIds = useSelector(({ openFolderIds }: RootState) => openFolderIds[openFoldersNamespaceKey] || [])
  const forceRefresh = useSelector(({ forceFinderRefresh }: RootState) => forceFinderRefresh)
  const queryClient = useQueryClient()
  const [loadingState, setLoadingState] = useState<FetchRequestState>(FetchRequestState.Loading)
  const [prefetchedSchema, setPrefetchedSchema] = useState<Omit<FilesView, 'files'>>()
  const [filesView, setFilesView] = useState<TreeFilesView>()
  const [showWarmState, setShowWarmState] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<TreeFile[]>([])
  const [lastCheckedIndex, setLastCheckedIndex] = useState<number | null>(null)
  const [dropDestination, setDropDestination] = useState<TreeFile | null>(null)
  const [dragging, setDragging] = useState(false)
  const [showEmptyState, setShowEmptyState] = useState(false)
  const [selectedColumnIndex, setSelectedColumnIndex] = useState<number | null>(null)
  const showExpandButton = !!handbook
  const [filesCounts, setFilesCounts] = useState<FilesCounts>()
  const { loadAssignedProperties } = useLoadAssignedProperties()

  const { initializeColumnWidths, setColumnWidths } = useColumnWidthsStore()
  const confirmationModalStore = ConfirmationModalStore
  const schemaId = useRouteSchemaId()
  const viewType = filesView?.schema?.default_view ?? prefetchedSchema?.schema?.default_view
  const currentSessionUuid = getUniqueIdentifier()

  useEffect(() => {
    confirmationModalStore.setState({
      parentId: selectedFiles[0]?.parent_id || '',
    })
    if (!selectedFiles.length) return

    setSelectedColumnIndex(null)
  }, [selectedFiles, confirmationModalStore])

  useEffect(() => {
    if (selectedColumnIndex === null) return

    setSelectedFiles([])
  }, [selectedColumnIndex])

  useFilesSchema(
    {
      location,
      workspaceId,
      query: {
        current_schema_id: schemaId,
      },
    },
    {
      onSuccess: (resp) => {
        if (resp.data.schema) {
          setPrefetchedSchema(resp.data)
        }
      },
    },
  )

  const filesResult = useFiles(
    {
      location,
      workspaceId,
      query: {
        items: 20,
        current_schema_id: schemaId,
        load_children_preview_urls: viewType && viewType !== ViewType.Calendar,
      },
    },
    { enabled: false },
  )

  const filesCountsResult = useFilesCounts({ location, workspaceId, query: {} }, { enabled: false })

  useEffect(() => {
    if (!filesCountsResult.isSuccess) return

    if (filesCountsResult.data?.data) {
      setFilesCounts(filesCountsResult.data?.data?.counts)
    }
  }, [filesCountsResult])

  useEffect(() => {
    const empty = filesView?.files?.length === 0 && loadingState !== FetchRequestState.Loading

    if (empty) setShowWarmState(false)

    setShowEmptyState(empty)
  }, [filesView?.files?.length, loadingState])

  useEffect(() => {
    const colWidths: number[] = [
      filesView?.schema?.column_widths?.name || FIRST_COLUMN_DEFAULT_WIDTH,
      ...(filesView?.schema?.properties.map((p) => filesView?.schema?.column_widths?.[p.id] || COLUMN_MIN_WIDTH) ?? []),
    ]
    setColumnWidths(colWidths)
  }, [filesView?.schema?.properties, filesView?.schema?.column_widths, initializeColumnWidths, setColumnWidths])

  const fetchAllFilesCount = useCallback(
    async (files: File[]) => {
      if (!['root', 'your_files', 'shared', 'recent', 'imported', 'shared_with_me'].includes(location)) {
        // Do not show warm state within folders
        setShowWarmState(false)
        return
      }

      const count = files?.length

      if (!count) return

      setShowWarmState(count > 0 && count <= 5)
    },
    [location],
  )

  const reportFilesViewError = useCallback(() => {
    reportError('Failed to fetch files view', {
      severity: 'error',
      metaData: { workspaceId, folderId },
    })
    showErrorMessageToast('An error occurred! Our team has been notified and we are looking into it.')
    setLoadingState(FetchRequestState.Rejected)
  }, [folderId, workspaceId])

  const updateFilesView = useCallback(async () => {
    setLoadingState(FetchRequestState.Loading)
    const res = await filesResult.fetchNextPage()

    if (res.isSuccess && filesView) {
      const lastPageIndex = res.data.pages.length - 1
      const lastPage = res.data.pages[lastPageIndex] as SuccessResponse<FilesView>
      const schema = lastPage.data.schema
      const default_schema = lastPage.data.default_schema
      const all_schemas = lastPage.data.all_schemas
      const files = lastPage.data.files
      const propertiesIds = schema.properties.map(({ id }: { id: number }) => id)
      schema.workflow_property?.id && propertiesIds.push(schema.workflow_property.id)

      setFilesView(
        (prev) =>
          prev && {
            schema,
            default_schema,
            all_schemas,
            files: [...prev.files, ...files],
          },
      )
      loadAssignedProperties(files, propertiesIds)
      setLoadingState(FetchRequestState.Resolved)

      if (viewType === ViewType.Board && parseInt(lastPage.headers['total-pages']) > res.data.pages.length)
        updateFilesView()

      return
    }

    if (res.isError) reportFilesViewError()
  }, [filesResult, filesView, loadAssignedProperties, reportFilesViewError, viewType])

  const loadMore = useCallback(async () => {
    if (!filesView || !filesResult || !filesResult.hasNextPage || filesResult.isFetching) return

    await updateFilesView()
  }, [filesResult, filesView, updateFilesView])

  useEffect(() => {
    if (viewType === ViewType.Board) setTimeout(loadMore, 200)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewType])

  const updateAllSchemas = (all_schemas: MinimalFilesViewSchema[]) => {
    if (!filesView) return

    if (folderId) schemasStore.add(folderId, all_schemas)

    setFilesView({
      ...filesView,
      all_schemas,
    })
  }

  const updateDefaultSchema = (default_schema: FilesViewSchema, all_schemas?: MinimalFilesViewSchema[]) => {
    if (!filesView) return

    let newFilesView = {
      ...filesView,
      schema: default_schema,
      default_schema,
    }

    if (all_schemas) {
      newFilesView.all_schemas = all_schemas
    }

    setFilesView(newFilesView)
    dispatch(upsertProperties(default_schema.properties))
  }

  const setActiveSchema = (schema: FilesViewSchema, all_schemas?: MinimalFilesViewSchema[]) => {
    if (!filesView) return

    let newFilesView = {
      ...filesView,
      schema,
    }

    if (all_schemas) {
      newFilesView.all_schemas = all_schemas
    }

    setFilesView(newFilesView)
    dispatch(upsertProperties(schema.properties))
  }

  const updateSchema = async (
    updateAttrs: Partial<SchemaAttributes>,
    { doRefresh = true }: { doRefresh?: boolean } = {},
  ) => {
    const {
      id,
      name,
      calendar_day_grid_view,
      default_view,
      sorting_property_id,
      sorting_direction,
      grouping_property_id,
      grouping_direction,
      column_widths,
      properties,
      historical_properties,
      filters,
      show_tasks,
      calendar_property_id,
    } = // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      filesView!.schema // We know the schema is loaded at this point.

    const otherAttributes = {
      id,
      name,
      default_view,
      calendar_day_grid_view,
      sorting_property_id,
      sorting_direction,
      grouping_property_id,
      grouping_direction,
      column_widths,
      properties,
      historical_properties,
      filters,
      show_tasks,
      calendar_property_id,
    }

    const promise = FilesViewsClient.updateSchema(
      {
        ...otherAttributes,
        ...updateAttrs,
      },
      workspaceId,
      folderId,
    )

    if (doRefresh) promise.then(() => refresh())

    // Set schema every time we update it in the backend
    promise.then((res) => {
      if (res.type === SUCCESS_RESPONSE_TYPE && res.data.files_view_schema)
        setFilesView(
          (prev) =>
            prev && {
              ...prev,
              schema: res.data.files_view_schema,
            },
        )
    })

    return promise
  }

  const loadFilesView = useCallback(
    async (loadProperties: boolean = true) => {
      setLoadingState(FetchRequestState.Loading)
      queryClient.resetQueries(['workspace', workspaceId, 'files', location])
      filesCountsResult.refetch()
      const res = await filesResult.refetch()

      if (res.isSuccess) {
        const schema = res.data.pages[0].data.schema
        const default_schema = res.data.pages[0].data.default_schema
        const files = res.data.pages[0].data.files
        const all_schemas = res.data.pages[0].data.all_schemas
        const propertiesIds = schema.properties.map(({ id }: { id: number }) => id)
        schema.workflow_property?.id && propertiesIds.push(schema.workflow_property.id)

        setFilesView({ default_schema, schema, files, all_schemas })

        const propertyPayload = schema.workflow_property?.id
          ? [...schema.properties, schema.workflow_property]
          : schema.properties
        dispatch(upsertProperties(propertyPayload))
        fetchAllFilesCount(files)
        if (schema.accessing_as_super_admin) {
          showToastMessage('You are managing this folder because you are a Super Admin', 'success')
        }

        loadProperties && loadAssignedProperties(files, propertiesIds)
        setLoadingState(FetchRequestState.Resolved)

        return
      }

      if (res.isIdle) {
        setLoadingState(FetchRequestState.Resolved)
      }

      if (res.isError) reportFilesViewError()
    },
    [
      dispatch,
      fetchAllFilesCount,
      filesCountsResult,
      filesResult,
      loadAssignedProperties,
      location,
      queryClient,
      reportFilesViewError,
      workspaceId,
    ],
  )

  const hasMore = () => {
    if (filesResult) return !!filesResult.hasNextPage
    return false
  }

  const selectFiles = useCallback(
    (files: TreeFile[], selected: boolean = true, keepOthers: boolean = false) => {
      const selection = uniqBy(files, ({ id, mention_id }) => id + mention_id)

      const others = differenceBy(selectedFiles, selection, 'id')

      if (selected) {
        setSelectedFiles(uniqBy([...(keepOthers ? others : []), ...selection], 'id'))
      } else {
        setSelectedFiles(others)
      }
    },
    [selectedFiles],
  )

  const resetSelection = useCallback(() => {
    selectFiles([])
  }, [selectFiles])

  /*
   * Add a property to the schema. `property` can be already present in the
   * database or being created on the fly here, so we handle both cases in the
   * same method.
   */
  const addPropertyToSchema = async (property: Property, position?: number) => {
    if (!workspaceKey || !filesView) {
      return
    }
    let propertyId = property.id

    // Create the property first if it's a new property
    if (!propertyId) {
      const res = await PropertyService.createProperty(workspaceKey, property)
      if (res.type === SUCCESS_RESPONSE_TYPE) {
        propertyId = res.data.property.id
      }
    }

    // Attach property to schema
    const res = await FilesViewsClient.createProperty(
      { propertyId, position },
      workspaceId,
      folderId,
      filesView.schema.id,
    )
    if (res.type === SUCCESS_RESPONSE_TYPE) {
      setFilesView({
        ...filesView,
        schema: res.data.files_view_schema,
      })
      dispatch(upsertProperties(res.data.files_view_schema.properties))
    }
    loadAssignedProperties(flatTree, [propertyId])
  }

  const updateSchemaProperty = async (propertyId: number, position: number) => {
    if (!filesView) return

    const res = await FilesViewsClient.updateProperty(
      { propertyId, position },
      workspaceId,
      folderId,
      filesView.schema.id,
    )

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      setFilesView((fv) => {
        if (!fv) return
        return {
          ...fv,
          schema: res.data.files_view_schema,
        }
      })
    }
  }

  const removePropertyFromSchema = async (propertyId: number) => {
    if (!filesView) return

    const isCurrentSortingProperty = filesView.schema.sorting_property_id === propertyId
    const isCurrentGroupingProperty = filesView.schema.grouping_property_id === propertyId
    const res = await FilesViewsClient.destroyProperty(propertyId, workspaceId, folderId, filesView.schema.id)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      setFilesView((fv) => {
        if (!fv) return
        return {
          ...fv,
          schema: res.data.files_view_schema,
        }
      })
    }
    if (isCurrentSortingProperty || isCurrentGroupingProperty) {
      refresh()
    }
  }

  const removePropertyFromHistorical = async (propertyId: number) => {
    if (!filesView) return

    const res = await FilesViewsClient.destroyHistoricalProperty(propertyId, workspaceId, folderId, filesView.schema.id)

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      setFilesView((fv) => {
        if (!fv) return
        return {
          ...fv,
          schema: res.data.files_view_schema,
        }
      })
    }
  }

  const updatePropertyValues = async (propertyId: number, values: PropertyValue[]) => {
    if (!filesView) return

    const updatedProperties = filesView.schema.properties.map((property) => {
      if (property.id === propertyId) {
        return {
          ...property,
          property_values: values,
        }
      }

      return property
    })

    dispatch(upsertProperties(updatedProperties))

    setFilesView((fv) => {
      if (!fv) return
      return {
        ...fv,
        schema: {
          ...fv.schema,
          properties: updatedProperties,
        },
      }
    })
  }

  const getAssigneesForUpdate = (propertyValue: PropertyValue): Assignee[] => {
    let assignees: Assignee[] = []

    Object.keys(filesProperties).forEach((fileId) => {
      const fileProperty = filesProperties[fileId]
      const propertyValues = fileProperty[propertyValue.property_id]?.values

      if (!propertyValues) return

      propertyValues.forEach((value) => {
        if (value.id === propertyValue.id && value.property_id === propertyValue.property_id) {
          filesView?.files.forEach((file) => {
            if (file.id === fileId) {
              assignees.push(fileToAssignee(file))
            }
          })
        }
      })
    })

    return assignees
  }

  const handleSort = async (sortingDescriptor: SortingDescriptor) => {
    if (!viewType) return

    await updateSchema(sortingDescriptor)
  }

  const refresh = useCallback(
    (loadProperties: boolean = true) => {
      resetSelection()
      loadFilesView(loadProperties)
    },
    [loadFilesView, resetSelection],
  )

  const refreshAfterExternalChange = useLocalDebouncedFn(
    ({ session_uuid }: RefreshPayload) => {
      if (session_uuid && session_uuid === currentSessionUuid) return

      refresh()
      handbooksStore.refresh(handbook?.key)
    },
    300, // Debounce interval
    {},
    [refresh, handbooksStore, currentSessionUuid],
  )

  const debouncedRefreshSchemas = useLocalDebouncedFn(
    async ({ session_uuid }: RefreshPayload) => {
      if (!folderId) return
      if (session_uuid && auth?.id && session_uuid === currentSessionUuid) return

      const res = await FilesViewsClient.getSchemas(workspaceId, folderId)

      if (res.type === SUCCESS_RESPONSE_TYPE) {
        updateAllSchemas(res.data.files_view_schemas)
      }
    },
    300, // Debounce interval
    {},
    [workspaceId, folderId, updateAllSchemas, auth?.id],
  )

  useFileViewUpdateRealtime({
    filesView,
    onUpdateMessage: refreshAfterExternalChange,
  })

  useItemsRealtime({
    folderId,
    onItemsMessage: refreshAfterExternalChange,
  })

  useViewsChannelRealtime({
    folderId,
    onViewsMessage: debouncedRefreshSchemas,
  })

  const asyncRefresh = async () => {
    resetSelection()
    await loadFilesView()
  }

  useEffect(() => {
    if (forceRefresh) {
      refresh()
      dispatch(clearFinderRefresh())
    }
  }, [dispatch, forceRefresh, refresh])

  useEffect(() => {
    if (!filesView) loadFilesView(true)
  }, [workspaceId, folderId, auth?.superAdminMode, prefetchedSchema?.schema?.default_view, filesView]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!schemaId || schemaId === 0) return
    if (!filesView?.schema.id) return
    if (filesView?.schema.id === schemaId) return
    if (filesResult.isLoading) return
    if (loadingState === FetchRequestState.Loading) return

    refresh()
  }, [schemaId, filesView?.schema, filesResult.isLoading, refresh, loadingState])

  useEffect(() => {
    const { currentSchema } = schemasStore

    if (!filesView?.schema) {
      schemasStore.setCurrentSchema(undefined)
    }

    if (
      (!currentSchema && filesView?.schema) ||
      (filesView?.schema && currentSchema && filesView.schema.id !== currentSchema.id)
    ) {
      const {
        schema: { id, default_view: type, name },
      } = filesView
      schemasStore.setCurrentSchema({ id, type, name })
    }
  }, [filesView, schemasStore, filesView?.schema])

  const resetFileTreeQueries = () => {
    queryClient.invalidateQueries(['workspace', workspaceId, 'files'])
  }

  /*
   * Files operations
   */
  const onMoveRequest = (movedFiles: File[], destinationFileId: string | null, _newWorkspaceId: number) => {
    if (!filesView) return

    resetSelection()

    resetFileTreeQueries()
    removeFilesFromTree(movedFiles)

    // Move to workspace root
    if (!destinationFileId) {
      refresh()
      return
    }

    let destinationFile: WritableDraft<TreeFile> | null = null
    filesView.files.forEach((rootNode) => {
      if (destinationFile) return
      destinationFile = findNode(rootNode, destinationFileId)?.node || null
    })
    if (destinationFile) {
      forceFolderReload(destinationFile)
    }
  }

  const onCopyRequest = async (_copiedFiles: File[], _selectedFolderId: string | null, newWorkspaceId: number) => {
    const sameWorkspace = newWorkspaceId === workspaceId
    if (sameWorkspace) {
      await asyncRefresh()
    }
    resetSelection()
    resetFileTreeQueries()
  }

  const onDeleteRequest = (removedFiles: File[], folder?: AlmanacFile | null) => {
    resetSelection()

    removeFilesFromTree(removedFiles)
    if (folder) {
      resetFileTreeQueries()
      forceFolderReload(folder)
    }
  }

  const onChangeEmoji = (file: File, emoji: string | null | undefined, channel?: string | null | undefined) => {
    updateTree(file, (node) => {
      node.header_emoji = emoji
      node.channel_emoji = channel as ChannelType
    })

    file.type === 'article' && files?.updateAttrs(file.id, { emoji: emoji, channel_emoji: channel as ChannelType })
  }

  const onRename = (file: File, newName: string) => {
    if (!filesView) return

    updateTree(file, (node) => {
      node.title = newName
    })

    let response
    if (file.type === 'folder') {
      response = FolderClient.renameFolder(file.id, newName)
    }
    if (file.type === 'article') {
      response = ArticleService.updateArticle(file.id, { title: newName })
    }
    if (file.type === 'media_file' && file.parent_id && file.media_file_id) {
      response = MediaFileClient.update(file?.real_workspace_id, file.media_file_id, {
        ...file,
        title: newName,
      })
    }
    if (file.type === 'design') {
      response = DesignClient.update(file.id, { title: newName })
    }

    if (!response) return

    response.then((res) => {
      if (res && res.type === SUCCESS_RESPONSE_TYPE) {
        resetFileTreeQueries()
        refresh()
        if (['article', 'media_file'].includes(file.type)) {
          files?.updateAttrs(file.id, { title: newName })
        }

        const parentHandbookKey = last(file.ancestor_projects)?.handbook?.key
        const handbookTreeStore = handbook && handbooksStore.handbooks.get(parentHandbookKey || handbook?.key)
        handbookTreeStore?.refresh()
      }

      if (res && res.type !== SUCCESS_RESPONSE_TYPE) {
        updateTree(file, (node) => {
          node.title = file.title
        })

        const errors = buildErrorMessage(res.data.errors) || 'Something went wrong. Please try again.'
        showToastMessage(errors, 'error')
      }
    })
  }

  /*
   * Drag & Drop
   *
   * The library we use for drag and drop only works with flat lists, so all
   * these operations are done on a flatten version of the tree.
   */
  const flattenTree = (root: TreeFile): any => {
    if (!root) return []
    if (!root.children) {
      return [root]
    }
    if (root.children) {
      return [root, root.children.map(flattenTree)]
    }
    return []
  }

  const flatTree = flattenDeep<TreeFile>(filesView?.files.map(flattenTree) || [])

  const handleDragStart = (result: DragStart) => {
    const { draggableId } = result
    if (!filesView) return

    const draggedFile = flatTree.find((node) => node.id === draggableId)

    if (draggedFile && !selectedFiles.includes(draggedFile)) {
      selectFiles([...selectedFiles, draggedFile])
    }

    if (draggedFile) {
      const destination = flatTree.find((node) => node.id === draggedFile.parent_id) ?? null
      setDropDestination(destination)
    }

    setDragging(true)
  }

  const handleDragUpdate = async (result: DropResult) => {
    if (!filesView || !result.destination) return

    const destinationFile = flatTree[result.destination.index]
    if (!destinationFile) return

    const destinationParent = flatTree.find((node) => node.id === destinationFile.parent_id) ?? null

    if (destinationFile.type === 'folder') {
      return setDropDestination(destinationFile)
    }

    return setDropDestination(destinationParent)
  }

  /*
   * The result passed by the drag and drop library does not give us any
   * information about the row itself, but only its index. To find the
   * destination folder, we have to find the file by index on the flat tree.
   */
  const handleDragEnd = async (result: DropResult) => {
    if (!filesView) return
    setDragging(false)
    resetSelection()

    setDropDestination(null)

    if (selectedFiles.length === 0) return
    if (result.reason === 'CANCEL') return

    const destinationFile = dropDestination
    if (destinationFile && !destinationFile.has_edit_access) {
      return
    }

    if (!destinationFile) return

    // Dragging a project/folder into a project is not allowed
    if (destinationFile.database_mode && some(selectedFiles, { type: 'folder' })) return

    // Dragging a file to the same location it's already in should do nothing
    if (selectedFiles.find((f) => f.ancestor_projects[f.ancestor_projects.length - 1]?.id === destinationFile.id))
      return

    // Save a copy of the current tree in case an error occurs, so we can roll
    // back to the previous state.
    const rollbackFiles = [...filesView.files]

    const numFiles = selectedFiles.length

    const submitMove = async () => {
      // Optimistically remove the files from their origin.
      removeFilesFromTree(selectedFiles)
      const response = await FolderClient.bulkMoveFiles({
        folderIds: selectedFiles.filter(({ type }) => type === 'folder').map(({ id }) => id),
        articleIds: selectedFiles.filter(({ type }) => type === 'article' || type === 'handbook').map(({ id }) => id),
        fileIds: selectedFiles.filter(({ type }) => type === 'media_file' || type === 'design').map(({ id }) => id),
        destinationFolderId: destinationFile?.id || null,
        destinationWorkspaceId: workspaceId,
        // TODO: Fix this the next time the file is edited.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        originalWorkspaceId: workspaceId!,
      })

      if (response.type === SUCCESS_RESPONSE_TYPE) {
        showToastMessage(
          <span>
            <span>
              <span role="img" aria-label="celebration horn">
                🎉
              </span>{' '}
              <b>{numFiles}</b> {pluralize('item', numFiles)} moved to <b>{destinationFile?.title}</b>!
            </span>
          </span>,
          'success',
          destinationFile?.handbook?.seo_string
            ? {
                variant: 'proceed',
                type: 'link',
                to: getFolderPath({
                  folderSlug: destinationFile.handbook.seo_string,
                }),
                label: 'Go to Project',
              }
            : undefined,
        )
        resetSelection()

        if (destinationFile) {
          // Remove currently assigned properties cache for moved files
          filesView.schema.properties.forEach(({ id }) => {
            dispatch(deleteFilesProperties(selectedFiles.map(fileToAssignee), id))
          })
          forceFolderReload(destinationFile)
        } else {
          refresh()
        }

        resetFileTreeQueries()

        return
      }

      if (response.type === 'error') {
        setFilesView({
          ...filesView,
          files: rollbackFiles,
        })
        reportError('Failed to move files', {
          severity: 'error',
          metaData: {
            filesIds: selectedFiles.map(({ id }) => id),
            destinationFile: destinationFile?.id,
          },
        })
        showErrorMessageToast('The files could not be moved')
        setLoadingState(FetchRequestState.Rejected)
      }
    }

    confirmationModalStore.setState({
      isConfirmationModalOpen: true,
      selectedFolder: destinationFile || null,
      selectedFiles: selectedFiles,
      movingToRoot: !destinationFile,
      destinationKey: destinationFile?.id,
      destinationTitle: destinationFile?.title,
      submit: submitMove,
      newWorkspaceId: workspaceId,
    })
  }

  /*
   * Selection
   */
  const selectableFiles = () => {
    return flatTree
  }

  const handleToggleSelectAll = (selected: boolean) => {
    if (!filesView) return

    if (!selected) {
      resetSelection()
    } else {
      selectFiles(selectableFiles())
    }
  }

  const allSelected = selectedFiles.length === flatTree.length && selectedFiles.length > 0

  /*
   * Interactions
   */
  const removeFilesFromTree = (files: File[]) => {
    setFilesView((fv) => {
      if (!fv) return
      return {
        ...fv,
        files: produce(fv.files, (draft) => {
          files.forEach((file) => {
            let result = null as NullableTreeNode
            draft.forEach((rootNode) => {
              if (result) return
              result = findNode(rootNode, file.id)
            })

            if (!result) return
            const removedNode = result.node
            // Remove from parent branches if we are removing a branch
            const parent = result.parent
            if (
              parent &&
              (removedNode.type === 'article' || removedNode.type === 'handbook') &&
              !removedNode.master_branch
            ) {
              const branchIndexToRemove = parent.branches?.findIndex((branch) => branch.id === removedNode.id)
              if (typeof branchIndexToRemove !== 'undefined' && branchIndexToRemove >= 0) {
                parent.branches?.splice(branchIndexToRemove, 1)
              }
            }
            // Remove from parent children list
            const parentList = parent?.children || draft
            const indexToRemove = parentList.indexOf(result.node)
            parentList.splice(indexToRemove, 1)
          })
        }),
      }
    })
  }

  const updateTree = (file: TreeFile, callback: (node: TreeFile) => void) => {
    setFilesView((fv) => {
      if (!fv) return
      return {
        ...fv,
        files: produce(fv.files, (draft) => {
          let node: WritableDraft<TreeFile> | null = null

          draft.forEach((rootNode) => {
            if (node) return
            node = findNode(rootNode, file.id)?.node || null
          })

          if (!node) return

          callback(node)
        }),
      }
    })
  }

  const findNode = (
    root: WritableDraft<TreeFile>,
    id: string,
    parent: WritableDraft<TreeFile> | null = null,
  ): NullableTreeNode => {
    if (root.id === id) {
      return { node: root, parent }
    }
    if (root.children) {
      let result = null
      for (let i = 0; i < root.children.length; i++) {
        result = findNode(root.children[i], id, root)
        if (result) break
      }
      return result
    }
    return null
  }

  const appendLoadedChildrenToFile =
    (file: File) =>
    (nuke: boolean = false) => {
      if (!file.children || !filesView) return

      const propertiesIds = filesView.schema.properties.map(({ id }) => id)
      filesView.schema.workflow_property?.id && propertiesIds.push(filesView.schema.workflow_property.id)
      loadAssignedProperties(file.children, propertiesIds)
      updateTree(file, (node) => appendChildrenUpdateTree(file, node, nuke, file.children))
    }

  const appendChildrenToFile =
    (file: File) =>
    (response: SuccessResponse<FilesView>, parentClient: PaginatedApiClient<FilesView>, nuke: boolean = false) => {
      if (!filesView) return

      const propertiesIds = filesView.schema.properties.map(({ id }) => id)
      response.data.schema.workflow_property?.id && propertiesIds.push(response.data.schema.workflow_property.id)
      loadAssignedProperties(response.data.files, propertiesIds)
      updateTree(file, (node) => appendChildrenUpdateTree(file, node, nuke, response.data.files, parentClient))
    }

  const appendChildrenUpdateTree = (
    file: File,
    node: TreeFile,
    nuke: boolean,
    files: File[] | undefined,
    parentClient?: PaginatedApiClient<FilesView>,
  ) => {
    if (!files) return

    node.forceReload = false
    if (nuke && node.children) {
      node.children = files.map((f) => {
        return (
          // Only reload first level children (this keeps children folders
          // open and avoids jittering)
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          node.children!.find(({ id }) => f.id === id) || {
            ...f,
            parentClient: parentClient,
            appendSiblings: appendChildrenToFile(file),
          }
        )
      })
      return
    }
    node.children = (node.children || []).concat(
      files.map((f) => {
        return {
          ...f,
          parentClient: parentClient,
          appendSiblings: appendChildrenToFile(file),
        }
      }),
    )
  }

  const openDocument = (file: File) => {
    updateTree(file, (node) => {
      node.children = [...(node.branches || [])]
    })
  }

  const openFolder = (file: File) => {
    if (!filesView) {
      return
    }
    dispatch(setOpenFolders(openFoldersNamespaceKey, [...openFolderIds, file.id]))
    const {
      schema: { sorting_direction, sorting_property_id },
    } = filesView
    const client = FilesViewsClient.show(workspaceId, file.id, {
      sorting_direction,
      sorting_property_id,
    })
    return { client, appendChildren: appendChildrenToFile(file) }
  }

  const openListFile = (file: File) => {
    if (file.type !== 'folder') {
      openDocument(file)

      return
    }

    if (!filesView) {
      return
    }
    dispatch(setOpenFolders(openFoldersNamespaceKey, [...openFolderIds, file.id]))
    return { appendChildren: appendLoadedChildrenToFile(file) }
  }

  const closeListFile = (file: File) => {
    dispatch(
      setOpenFolders(
        openFoldersNamespaceKey,
        openFolderIds.filter((id: string) => file.id !== id),
      ),
    )
  }

  const openFile = (file: File) => {
    if (file.type === 'folder') {
      return openFolder(file)
    } else {
      openDocument(file)
    }
  }

  const closeFile = (file: File) => {
    dispatch(
      setOpenFolders(
        openFoldersNamespaceKey,
        openFolderIds.filter((id: string) => file.id !== id),
      ),
    )
    updateTree(file, (node) => {
      delete node.children
    })
  }

  const forceFolderReload = (file: File) => {
    updateTree(file, (node) => {
      node.forceReload = true
    })
  }

  const isSelected = (file: File) => {
    return selectedFiles.map(({ id, mention_id }) => id + mention_id).includes(file.id + file.mention_id)
  }

  const handleClick = (file: File, shiftKey: boolean) => {
    if (!filesView) return

    const shouldSelect = !isSelected(file)
    const fileIndex = findIndex(flatTree, ({ id }) => id === file.id)

    if (shiftKey && shouldSelect && lastCheckedIndex !== null) {
      const [a, b] = [lastCheckedIndex, fileIndex].sort()
      const filesToSelect = flatTree.slice(a, b + 1)
      setLastCheckedIndex(fileIndex)
      selectFiles([...selectedFiles, ...filesToSelect])
    } else if (shouldSelect) {
      setLastCheckedIndex(fileIndex)
      selectFiles([...selectedFiles, file])
    } else {
      selectFiles(selectedFiles.filter(({ id, mention_id }) => id + mention_id !== file.id + file.mention_id))
    }
  }

  const onlyDocsAndHandbooksSelected = useMemo(
    () => selectedFiles.every((f: File) => ['article', 'handbook'].includes(f.type)),
    [selectedFiles],
  )
  const canEditAllSelected = useMemo(
    () => selectedFiles.every((f: File) => f.has_edit_access && !f.mention_id),
    [selectedFiles],
  )
  const canMoveSelection = useMemo(
    () => onlyDocsAndHandbooksSelected && canEditAllSelected,
    [onlyDocsAndHandbooksSelected, canEditAllSelected],
  )
  const canCopySelection = useMemo(() => !selectedFiles.find((f) => f.mention_id), [selectedFiles])
  const canDeleteSelection = useMemo(() => canEditAllSelected, [canEditAllSelected])
  const canShareSelection = useMemo(
    () => selectedFiles.length === 1 && !selectedFiles.find((f) => f.mention_id),
    [selectedFiles],
  )
  const canAddToHandbook = useMemo(
    () => selectedFiles.every((f) => f.type === 'article' && !f.mention_id),
    [selectedFiles],
  )

  const quickAddFile = useCallback((file: TreeFile) => {
    setFilesView(
      (prev) =>
        prev && {
          ...prev,
          files: [...prev.files, file],
        },
    )
  }, [])

  const value = {
    filesView,
    filesCounts,
    loadingState,
    addPropertyToSchema,
    updateSchema,
    updateAllSchemas,
    updateDefaultSchema,
    setActiveSchema,
    updateSchemaProperty,
    removePropertyFromSchema,
    removePropertyFromHistorical,
    updatePropertyValues,
    getAssigneesForUpdate,
    handleSort,
    viewType,
    refresh,
    refreshAfterExternalChange,
    loadMore,
    hasMore: hasMore(),
    filesResult,

    // Selection
    selectedFiles,
    allSelected,
    selectFiles,
    handleToggleSelectAll,
    resetSelection,
    selectedColumnIndex,
    setSelectedColumnIndex,

    // Files operations
    onMoveRequest,
    onCopyRequest,
    onDeleteRequest,
    onRename,
    onChangeEmoji,
    canCopySelection,
    canMoveSelection,
    canDeleteSelection,
    canShareSelection,
    canAddToHandbook,
    quickAddFile,

    // Drag & Drop
    handleDragStart,
    handleDragEnd,
    handleDragUpdate,
    dropDestination,
    dragging,

    // Interactions
    openFile,
    closeFile,
    handleClick,
    openListFile,
    closeListFile,

    // Introspection
    isSelected,
    openFolderIds,
    showWarmState,
    showEmptyState,
    showExpandButton,
  }

  return <FinderContextProvider value={value}>{children}</FinderContextProvider>
}

export const useFinderContext = () => {
  return useContext(FinderContext)
}
